import { memo, useMemo } from 'react';
import { Button, Chip, CircularProgress, Stack } from '@mui/material';
import { AddCircleOutline, CancelOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useToggle } from '@dfl/hook-utils';
import { ConfirmAction } from 'components/ConfirmAction';
import useAcceptBusinessAccountRequest from '../../hooks/useAcceptBusinessAccountRequest';
import { BusinessAccountRequestRejectModal } from '../BusinessAccountRequestRejectModal';
import { BUSINESS_ACCOUNT_REQUEST_STATUS_ENUM, IBusinessAccountRequest } from '../../interfaces';

type BusinessAccountRequestRowActionsProps = {
  record: IBusinessAccountRequest;
  asButtons?: boolean;
};

const BusinessAccountRequestRowActions = ({ record, asButtons }: BusinessAccountRequestRowActionsProps) => {
  const { t } = useTranslation('businessAccountRequest');
  const { isOpen: isAcceptOpen, onClose: onAcceptClose, onOpen: onAcceptOpen } = useToggle();
  const { isOpen: isRejectOpen, onClose: onRejectClose, onOpen: onRejectOpen } = useToggle();

  const { mutate: accept, isLoading: isAccepting } = useAcceptBusinessAccountRequest(record._id, onAcceptClose);

  const resolved = useMemo(
    () =>
      [BUSINESS_ACCOUNT_REQUEST_STATUS_ENUM.ACCEPTED, BUSINESS_ACCOUNT_REQUEST_STATUS_ENUM.REJECTED].includes(
        record?.status,
      ),
    [record],
  );

  return (
    <Stack direction='row' spacing={1}>
      {asButtons ? (
        <>
          <Button
            variant='outlined'
            color='primary'
            startIcon={isAccepting ? <CircularProgress size={15} /> : <AddCircleOutline />}
            onClick={onAcceptOpen}
            disabled={resolved || isAccepting}
          >
            {t('accept')}
          </Button>
          <Button
            variant='outlined'
            color='error'
            startIcon={<CancelOutlined />}
            onClick={onRejectOpen}
            disabled={resolved}
          >
            {t('reject')}
          </Button>
        </>
      ) : (
        <>
          <Chip
            color='primary'
            icon={isAccepting ? <CircularProgress size={15} /> : <AddCircleOutline />}
            label={t('accept')}
            variant='outlined'
            size='small'
            onClick={onAcceptOpen}
            clickable={!isAccepting}
            disabled={resolved}
          />
          <Chip
            color='error'
            icon={<CancelOutlined />}
            label={t('reject')}
            variant='outlined'
            size='small'
            onClick={onRejectOpen}
            clickable
            disabled={resolved}
          />
        </>
      )}

      <ConfirmAction
        open={isAcceptOpen}
        onClose={onAcceptClose}
        onConfirm={() => {
          accept();
        }}
        isLoading={isAccepting}
        title={t('confirm.title')}
        confirmation={t('confirm.confirmation')}
      />

      <BusinessAccountRequestRejectModal open={isRejectOpen} onClose={onRejectClose} requestId={record._id} />
    </Stack>
  );
};

export default memo(BusinessAccountRequestRowActions);
